import React, { useRef, useState, useEffect, useMemo, memo, useCallback,  } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Checkbox,
    FormControlLabel,
    Stack,
    Divider,
    Alert,
    Button,
    Tooltip,
    IconButton
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useMutation, useQuery } from "@tanstack/react-query";
import { 
    Delete as DeleteIcon, 
    Add as AddIcon, 
    Info as InfoIcon,
    ArrowBack as ArrowBackIcon 
} from '@mui/icons-material';
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";

export interface FieldMappingType {
    csvColumn: string;
    mappedField: string;
    customFieldName?: string;
}

const normalizeFieldName = (value?: string): string => (value || '').trim().toLowerCase();

export interface FieldMappingProps {
    source?: 'csv' | 'hubspot' | 'google sheets';
    sourceHeaders: string[]; // Updated name from csvHeaders to safely match your dynamic usage
    csvData?: string[][];
    initialMappings?: FieldMappingType[];
    hasUnsubscribeColumn: boolean;
    unsubscribeColumnMapping?: string;
    onMappingsChange: (mappings: FieldMappingType[]) => void;
    onUnsubscribeChange: (hasUnsubscribe: boolean, columnMapping?: string) => void;
    
    // Added Missing Integration Fields
    integrationTypeId?: string;
    isEmailColFilled?: boolean;
    setIsEmailColFilled?: (filled: boolean) => void;
    isSubmitAttempted?: boolean;
    workspaceId: number | undefined;
}

// Predefined field options in the required order
const PREDEFINED_FIELDS = [
    { value: 'Email Address', label: 'Email Address', required: true },
    { value: 'First Name', label: 'First Name', required: false },
    { value: 'Last Name', label: 'Last Name', required: false },
    { value: 'Company Name', label: 'Company Name', required: false },
    { value: 'Website', label: 'Website', required: false },
    { value: 'Phone Number', label: 'Phone Number', required: false },
    { value: 'LinkedIn Profile', label: 'LinkedIn Profile', required: false },
    { value: 'Location', label: 'Location', required: false }
];

// Auto-matching patterns for CSV headers
const AUTO_MATCH_PATTERNS: Record<string, Record<string, string[]>> = {
    csv: {
        'First Name': ['first name', 'fname', 'f name', 'firstname', 'given name'],
        'Last Name': ['last name', 'lname', 'l name', 'lastname', 'surname', 'family name'],
        'Email Address': ['email', 'email address', 'e-mail', 'mail', 'email_address'],
        'Phone Number': ['phone', 'phone number', 'mobile', 'cell', 'telephone', 'tel'],
        'Company Name': ['company', 'company name', 'organization', 'org', 'business'],
        'Website': ['website', 'web site', 'url', 'site', 'homepage'],
        'LinkedIn Profile': ['linkedin', 'linkedin profile', 'linkedin url', 'li profile'],
        'Location': ['location', 'address', 'city', 'country', 'region', 'place']
    },
    hubspot: {
        'First Name': ['firstname'],
        'Last Name': ['lastname'],
        'Email Address': ['email'],
        'Phone Number': ['phone'],
        'Company Name': ['company'],
        'Website': ['website'],
        'LinkedIn Profile': ['hs_linkedin_url'],
        'Location': ['address']
    },
    'google sheets': {
        'First Name': ['first name', 'fname', 'firstname'],
        'Last Name': ['last name', 'lname', 'lastname'],
        'Email Address': ['email', 'email address', 'email_address'],
        'Phone Number': ['phone', 'phone number'],
        'Company Name': ['company', 'company name'],
        'Website': ['website', 'url'],
        'LinkedIn Profile': ['linkedin'],
        'Location': ['location', 'address']
    }
};


export default function AdminFieldMapping({
    source = 'csv',
    sourceHeaders = [],
    csvData = [],
    initialMappings = [],
    hasUnsubscribeColumn,
    unsubscribeColumnMapping,
    onMappingsChange,
    onUnsubscribeChange,
    integrationTypeId,
    isEmailColFilled,
    setIsEmailColFilled,
    isSubmitAttempted,
    workspaceId,
}: FieldMappingProps) {    
    const [mappings, setMappings] = useState<FieldMappingType[]>(initialMappings);
    const [unsubscribeEnabled, setUnsubscribeEnabled] = useState(hasUnsubscribeColumn);
    const [unsubscribeColumn, setUnsubscribeColumn] = useState(unsubscribeColumnMapping || '');
    const [customMappings, setCustomMappings] = useState<FieldMappingType[]>([]);
    
    const [hubspotPreviewData, setHubspotPreviewData] = useState<Record<string, any>>({});
    const [googleSheetsPreviewData, setGoogleSheetsPreviewData] = useState<Record<string, any>>({});
    const [noOfRows, setNoOfRows] = useState<number>(0);
    
    const onMappingsChangeRef = useRef(onMappingsChange);
    const lastEmittedMappingsRef = useRef<string>('');

    useEffect(() => {
        onMappingsChangeRef.current = onMappingsChange;
    }, [onMappingsChange]);

    const getSourceLabel = () => {
        if (source === 'hubspot') return 'HubSpot Property';
        if (source === 'google sheets') return 'Sheet Column';
        return 'CSV Column';
    };

    const autoMatchField = useCallback((header: string): string => {
        const headerLower = header.toLowerCase().trim();
        const absolutePatterns = AUTO_MATCH_PATTERNS[source] || AUTO_MATCH_PATTERNS['csv'];

        for (const [fieldKey, patterns] of Object.entries(absolutePatterns)) {
            if (patterns.some(pattern => headerLower.includes(pattern))) {
                return fieldKey;
            }
        }
        return '';
    }, [source]);

    // Initialize/Sync patterns cleanly
    useEffect(() => {
        if (sourceHeaders.length === 0) return;

        const initialMappingsByField = new Map<string, FieldMappingType>();
        for (const mapping of initialMappings || []) {
            if (!mapping?.mappedField) continue;
            initialMappingsByField.set(normalizeFieldName(mapping.mappedField), mapping);
        }

        const newMappings: FieldMappingType[] = [];
        const usedHeaders = new Set<string>();

        PREDEFINED_FIELDS.forEach(field => {
            const existingMapping = initialMappingsByField.get(normalizeFieldName(field.value));
            let matchedHeader = existingMapping?.csvColumn || '';

            if (!matchedHeader || !sourceHeaders.includes(matchedHeader) || usedHeaders.has(matchedHeader) || matchedHeader === unsubscribeColumn) {
                matchedHeader = '';
                for (const header of sourceHeaders) {
                    if (!usedHeaders.has(header) && header !== unsubscribeColumn) {
                        const autoMatched = autoMatchField(header);
                        if (autoMatched === field.value) {
                            matchedHeader = header;
                            break;
                        }
                    }
                }
            }

            if (matchedHeader) {
                usedHeaders.add(matchedHeader);
            }

            newMappings.push({
                csvColumn: matchedHeader,
                mappedField: field.value,
            });
        });

        const newCustomMappings: FieldMappingType[] = [];
        for (const mapping of initialMappings || []) {
            if (normalizeFieldName(mapping.mappedField) !== normalizeFieldName('Custom Field')) continue;

            const customCsvColumn = sourceHeaders.includes(mapping.csvColumn) &&
            !usedHeaders.has(mapping.csvColumn) &&
            mapping.csvColumn !== unsubscribeColumn
                ? mapping.csvColumn
                : '';

            if (customCsvColumn) {
                usedHeaders.add(customCsvColumn);
            }

            newCustomMappings.push({
                mappedField: 'Custom Field',
                csvColumn: customCsvColumn,
                customFieldName: mapping.customFieldName || '',
            });
        }

        const combinedMappings = [...newMappings, ...newCustomMappings];
        const mappingSignature = JSON.stringify(combinedMappings);

        if (lastEmittedMappingsRef.current === mappingSignature) {
            return;
        }

        lastEmittedMappingsRef.current = mappingSignature;
        setMappings(newMappings);
        setCustomMappings(newCustomMappings);
        onMappingsChangeRef.current(combinedMappings);
    }, [sourceHeaders, unsubscribeColumn, source, initialMappings, autoMatchField]);

    const getAvailableCsvHeaders = useCallback((currentCsvColumn?: string) => {
        const allMappings = [...mappings, ...customMappings];
        const mappedColumns = allMappings
            .map(m => m.csvColumn)
            .filter(col => col && col !== currentCsvColumn);

        return sourceHeaders.filter(header =>
            !mappedColumns.includes(header) &&
            header !== unsubscribeColumn
        );
    }, [sourceHeaders, mappings, customMappings, unsubscribeColumn]);

    const GetHubspotData = useMutation({
        mutationKey: ["GetPreviewValue", integrationTypeId],
        mutationFn: () => authenticateAndPostData("/admin/hubspot/fetch-preview-value/", {
            hubspotListId: integrationTypeId,
            workspace_id: workspaceId,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: (res: any) => {
            const properties = res.data;
            setHubspotPreviewData(properties.contacts?.properties || {});
            setNoOfRows(properties.no_of_rows || 0);
        },
        onError: (err) => {
            console.log("Failed to get Hubspot Preview Value ", err);
        }
    });

    const GetGoogleSheetsData = useMutation({
        mutationKey: ['GetGoogleSheetsPreviewValue', integrationTypeId],
        mutationFn: () => authenticateAndPostData("/admin/google-sheets/fetch-preview-value/", {
            sheets_url: integrationTypeId,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: (res: any) => {
            const properties = res.data;
            setGoogleSheetsPreviewData(properties.preview || {});
            setNoOfRows(properties.no_of_rows || 0);
        },
        onError: (err) => {
            console.log("Failed to get Google Sheets Preview Value ", err);
        }
    });

    useEffect(() => {
        setIsEmailColFilled?.(false);
        if (source === "hubspot" && integrationTypeId) {
            setHubspotPreviewData({});
            GetHubspotData.reset();
            GetHubspotData.mutate();
            lastEmittedMappingsRef.current = '';
            setMappings([]);
            onMappingsChange([]);
        }
        if (source === 'google sheets' && integrationTypeId) {
            setGoogleSheetsPreviewData({});
            GetGoogleSheetsData.reset();
            GetGoogleSheetsData.mutate();
            lastEmittedMappingsRef.current = '';
            setMappings([]);
            onMappingsChange([]);
        }
    }, [source, integrationTypeId]);

    const getPreviewValue = useCallback((csvColumn: string): string => {
        if (!csvData || csvData.length === 0 || !csvColumn) return '';

        const columnIndex = sourceHeaders.indexOf(csvColumn);
        if (columnIndex === -1) return '';

        const firstRowWithData = csvData.find(row => row[columnIndex] && row[columnIndex].trim() !== '');
        return firstRowWithData ? firstRowWithData[columnIndex] : (csvData[0][columnIndex] || '');
    }, [csvData, sourceHeaders]);

    const handlePredefinedMappingChange = useCallback((mappedField: string, newCsvColumn: string) => {
            const newMappings = mappings.map(m =>
                m.mappedField === mappedField ? { ...m, csvColumn: newCsvColumn } : m
            );
            setMappings(newMappings);
            onMappingsChange([...newMappings, ...customMappings]);
        },
        [mappings, customMappings, onMappingsChange]
    );

    const handleCustomMappingChange = useCallback((index: number, field: keyof FieldMappingType, value: string) => {
        const newCustomMappings = [...customMappings];
        newCustomMappings[index] = { ...newCustomMappings[index], [field]: value };
        setCustomMappings(newCustomMappings);
        onMappingsChange([...mappings, ...newCustomMappings]);
    }, [customMappings, mappings, onMappingsChange]);

    const handleAddCustomMapping = useCallback(() => {
        const available = getAvailableCsvHeaders();
        if (available.length > 0) {
            const newMapping: FieldMappingType = {
                csvColumn: '',
                mappedField: 'Custom Field',
                customFieldName: ''
            };
            const newCustom = [...customMappings, newMapping];
            setCustomMappings(newCustom);
            onMappingsChange([...mappings, ...newCustom]);
        }
    }, [getAvailableCsvHeaders, customMappings, mappings, onMappingsChange]);

    const handleRemoveCustomMapping = useCallback((index: number) => {
        const newCustom = customMappings.filter((_, i) => i !== index);
        setCustomMappings(newCustom);
        onMappingsChange([...mappings, ...newCustom]);
    }, [customMappings, mappings, onMappingsChange]);

    const handleUnsubscribeToggle = useCallback((checked: boolean) => {
        setUnsubscribeEnabled(checked);
        if (!checked) {
            setUnsubscribeColumn('');
            onUnsubscribeChange(false);
        } else {
            onUnsubscribeChange(true, unsubscribeColumn);
        }
    }, [unsubscribeColumn, onUnsubscribeChange]);

    const handleUnsubscribeColumnChange = useCallback((column: string) => {
        const isEnabled = !!column;
        setUnsubscribeEnabled(isEnabled);
        setUnsubscribeColumn(column);
        onUnsubscribeChange(isEnabled, column);
    }, [onUnsubscribeChange]);

    const isEmailMapped = !!mappings.find(m => m.mappedField === 'Email Address')?.csvColumn;

    const remotePreviewData = source === 'hubspot' ? hubspotPreviewData
        : source === 'google sheets' ? googleSheetsPreviewData
            : null;

    // Preview data arrives after the auto-mapping runs, so email validity is derived from
    // whichever column Email Address currently points at instead of being decided once.
    useEffect(() => {
        if (!remotePreviewData) return;

        const emailColumn = mappings.find(m => m.mappedField === 'Email Address')?.csvColumn;
        const emailValue = emailColumn ? remotePreviewData[emailColumn] : undefined;

        setIsEmailColFilled?.(isValidEmail(emailValue) && noOfRows > 0 && noOfRows <= 100000);
    }, [remotePreviewData, mappings, noOfRows, setIsEmailColFilled]);

    const isPreviewLoading =
        GetGoogleSheetsData.isPending || GetHubspotData.isPending ||
        (source === 'google sheets' && !!integrationTypeId && GetGoogleSheetsData.isIdle) ||
        (source === 'hubspot' && !!integrationTypeId && GetHubspotData.isIdle);

    const memoizedAvailableHeaders = useMemo(() => {
        const mapped = [...mappings, ...customMappings].map(m => m.csvColumn).filter(Boolean);
        return {
            forPredefined: (current: string, field: string) => sourceHeaders.filter(h =>
                h !== unsubscribeColumn &&
                (h === current || !mapped.includes(h) || mappings.find(m => m.csvColumn === h)?.mappedField === field)
            ),
            forCustom: (current: string, index: number) => sourceHeaders.filter(h =>
                h !== unsubscribeColumn &&
                (h === current ||
                    (!mappings.some(m => m.csvColumn === h) &&
                        !customMappings.some((m, i) => i !== index && m.csvColumn === h)))
            )
        };
    }, [sourceHeaders, mappings, customMappings, unsubscribeColumn]);

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Field Mapping ({source?.toUpperCase()})
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                Map your data source attributes to the system identifiers required for campaign synchronization.
            </Alert>

            {/* Crucial Validation Warnings */}
            {!isEmailMapped && !isPreviewLoading && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {`Email Address is required and must be mapped to a ${toTitleCase(source)} column.`}
                </Alert>
            )}

            {source !== "csv" && !isPreviewLoading && (
                <>
                    {isSubmitAttempted && !isEmailColFilled && (
                        <Alert id="email-error" severity="error" sx={{ mb: 3 }}>
                            The Email Address field cannot be empty and must be in a valid format.
                        </Alert>
                    )}

                    {noOfRows > 100000 && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            The Number of rows must be less than 100,000. Currently it has {noOfRows} rows.
                        </Alert>
                    )}
                </>
            )}

            {/* Unsubscribe Column Section */}
            {(source === "csv" || source === "google sheets") && (
                <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body1" fontWeight="medium">
                            Unsubscribe Column
                        </Typography>
                        <Tooltip 
                            title="Once you select the unsubscribe column, values like ‘True’ or ‘Yes’ will be detected. These contacts will be automatically marked as unsubscribed and skipped in the campaign."
                            placement="top"
                        >
                            <IconButton size="small">
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel id="unsubscribe-column-label">Unsubscribe Column</InputLabel>
                        <Select
                            labelId="unsubscribe-column-label"
                            value={unsubscribeColumn || "none"}
                            label="Unsubscribe Column"
                            onChange={(e) => handleUnsubscribeColumnChange(e.target.value === "none" ? "" : e.target.value)}
                        >
                            <MenuItem value="none">None</MenuItem>
                            {sourceHeaders.map((header, index) => {
                                const safeValue = header?.trim() ? header : `col-${index}`;
                                return (
                                    <MenuItem key={`${safeValue}-${index}`} value={safeValue}>
                                        {header?.trim() || `Column ${index + 1}`}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>

                    {/* Unsubscribe Previews */}
                    {source === "csv" && unsubscribeColumn && getPreviewValue(unsubscribeColumn) && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" display="block">Preview:</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {getPreviewValue(unsubscribeColumn)}
                            </Typography>
                        </Box>
                    )}

                    {source === "google sheets" && unsubscribeColumn && googleSheetsPreviewData?.[unsubscribeColumn] && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" display="block">Preview:</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {googleSheetsPreviewData?.[unsubscribeColumn]}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Predefined Field Mappings */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Predefined Fields
            </Typography>

            <Stack spacing={2} sx={{ mb: 3 }}>
                {mappings.map((mapping) => {
                    const field = PREDEFINED_FIELDS.find(f => f.value === mapping.mappedField);
                    const isRequired = field?.required || false;
                    const availableHeaders = memoizedAvailableHeaders.forPredefined(mapping.csvColumn, mapping.mappedField);

                    return (
                        <Box key={mapping.mappedField} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        Predefined Field {isRequired && <span style={{ color: 'red' }}>*</span>}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {mapping.mappedField}
                                    </Typography>
                                </Box>

                                <ArrowBackIcon sx={{ color: 'text.secondary' }} />

                                <Box sx={{ flex: 1 }}>
                                    <FormControl 
                                        fullWidth 
                                        required={isRequired} 
                                        error={isRequired && !mapping.csvColumn}
                                    >
                                        <InputLabel>{`${toTitleCase(source)} Column`}</InputLabel>
                                        <Select
                                            value={mapping.csvColumn || 'none'}
                                            label={`${toTitleCase(source)} Column`}
                                            onChange={(e) => handlePredefinedMappingChange(mapping.mappedField, e.target.value === 'none' ? '' : e.target.value)}
                                        >
                                            {!isRequired && <MenuItem value="none">None</MenuItem>}
                                            {availableHeaders.map((header) => (
                                                <MenuItem key={header} value={header}>
                                                    {header}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* Predefined Dynamic Previews */}
                                    {source === 'csv' && mapping.csvColumn && getPreviewValue(mapping.csvColumn) && (
                                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="caption" color="text.secondary" display="block">Preview:</Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                {getPreviewValue(mapping.csvColumn)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {source === 'hubspot' && mapping.csvColumn && hubspotPreviewData?.[mapping.csvColumn] && (
                                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="caption" color="text.secondary" display="block">Preview:</Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                {hubspotPreviewData?.[mapping.csvColumn]}
                                            </Typography>
                                        </Box>
                                    )}

                                    {source === 'google sheets' && mapping.csvColumn && googleSheetsPreviewData?.[mapping.csvColumn] && (
                                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="caption" color="text.secondary" display="block">Preview:</Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                {googleSheetsPreviewData?.[mapping.csvColumn]}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>

            {/* Custom Field Mappings */}
            {customMappings.length > 0 && (
                <>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        Custom Fields
                    </Typography>

                    <Stack spacing={2} sx={{ mb: 3 }}>
                        {customMappings.map((mapping, index) => {
                            const availableHeaders = memoizedAvailableHeaders.forCustom(mapping.csvColumn, index);
                            
                            return (
                                <Box key={index} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="center">
                                        <Box sx={{ flex: 1, width: '100%' }}>
                                            <TextField
                                                label="Custom Field Name"
                                                value={mapping.customFieldName || ''}
                                                onChange={(e) => handleCustomMappingChange(index, 'customFieldName', e.target.value)}
                                                fullWidth
                                                placeholder="Enter custom field name"
                                            />
                                        </Box>

                                        <ArrowBackIcon sx={{ color: 'text.secondary', display: { xs: 'none', lg: 'block' } }} />

                                        <Box sx={{ flex: 1, width: '100%' }}>
                                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                                <Box sx={{ flex: 1 }}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>{`${toTitleCase(source)} Column`}</InputLabel>
                                                        <Select
                                                            value={mapping.csvColumn || ''}
                                                            label={`${toTitleCase(source)} Column`}
                                                            onChange={(e) => handleCustomMappingChange(index, 'csvColumn', e.target.value)}
                                                        >
                                                            {availableHeaders.map((header) => (
                                                                <MenuItem key={header} value={header}>
                                                                    {header}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>

                                                    {/* Custom Dynamic Previews */}
                                                    {mapping.csvColumn && getPreviewValue(mapping.csvColumn) && source === 'csv' && (
                                                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                                            <Typography variant="caption" color="text.secondary" display="block">Preview:</Typography>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                                {getPreviewValue(mapping.csvColumn)}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {mapping.csvColumn && hubspotPreviewData?.[mapping.csvColumn] && source === 'hubspot' && (
                                                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                                            <Typography variant="caption" color="text.secondary" display="block">Preview:</Typography>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                                {hubspotPreviewData?.[mapping.csvColumn]}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {mapping.csvColumn && googleSheetsPreviewData?.[mapping.csvColumn] && source === 'google sheets' && (
                                                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                                            <Typography variant="caption" color="text.secondary" display="block">Preview:</Typography>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                                {googleSheetsPreviewData?.[mapping.csvColumn]}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>

                                                <IconButton 
                                                    color="error" 
                                                    onClick={() => handleRemoveCustomMapping(index)}
                                                    sx={{ mt: 1 }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </>
            )}

            {/* Add Custom Field Trigger Action */}
            {getAvailableCsvHeaders().length > 0 && (
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={handleAddCustomMapping}
                    sx={{ my: 2 }}
                >
                    Add Custom Field
                </Button>
            )}
        </Box>
    );
}

const toTitleCase = (str: string): string => {
    if (str === "csv") return "CSV";
    return str.toLowerCase().split(" ").filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

const isValidEmail = (value?: string): boolean => {
    if (!value || typeof value !== "string") return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value.trim());
};

